import { DateTimeEditor } from '@dataGrid/components/cellEditors';
import appConstants from "./../../../../appConstants";
import React from "react";
import { isValid as isDateTimeValid, format, isWeekend } from "date-fns";
import { Button, withStyles } from "@material-ui/core";
import CancelButton from "../components/Buttons/CancelButton";
import Footer from "../components/Footer";
import { redirectToIBPlanning } from "../utils";
import withRouter from "../../../../routes/withRouter";
import { getTerminalDateByOffset } from "../../Dispatch/utils/utils";
import { compose } from "recompose";
import { withUserProfile } from "../../../../userProfile/userProfileContext";
import { getDataWithQueryString } from "./../../../../api/componentAPIs";
import { CustomDateRenderer } from "./Days";
import CustomConfirmationDialog from "../../Dispatch/RouteExecution/DriverPopUp/common/CustomConfirmationDialog";
import {
  checkPlanAlreadyInitialized,
  getHolidayName,
  isHoliday,
  isPastDate,
} from "./utils";
import { withPlanDate } from "../PlanDateContext";
import { APP_DATE_FORMAT } from "../constants";

const styles = (theme) => ({
  container: {
    height: "100%",
  },
  dateContainer: {
    display: "flex",
    alignItems: "center",
    padding: 32,
    justifyContent : "center"
  },
  label: {
    marginRight: 16,
    paddingTop: 8,
    color: theme.HeaderSelectedText,
    fontWeight: 600,
    fontSize: 14,
  },
  actionButton: {
    textTransform: "none",
    "&$actionDisabled": {
      backgroundColor: theme.customInputBGColor,
      color: theme.TextColor,
      cursor: "not-allowed",
      opacity: 0.8,
    },
  },
  actionDisabled: {},
  confirmText : {
    color : theme.gridTextColor
  }
});

class PlanDateForm extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      planDate: this.props.planDate,
      holidays: [],
      showHolidayWarning: false,
    };
  }

  componentDidMount() {
    this.loadHolidays();
  }

  loadHolidays = () => {
    const {
      userData: { userProfile },
    } = this.props;
    getDataWithQueryString(
      "GetValidHolidays",
      `terminalId=${userProfile.terminal.id}`
    ).then((response) => {
      if (response?.status === 200) {
        this.setState({
          holidays: response.data,
        });
      }
    });
  };

  onPlanDateChanged = (value) => {
    if (!value) {
      this.setState({
        planDate: value,
      });
      return;
    }
    if (value && isDateTimeValid(value)) {
      this.setState({
        planDate: value,
      });
    }
  };

  handleNext = () => {
    const { planDate, holidays } = this.state;
    const { initializedDates } = this.props;
    if (
      isHoliday(planDate, holidays) &&
      !checkPlanAlreadyInitialized(planDate, initializedDates)
    ) {
      this.setState({
        showHolidayWarning: true,
      });
      return;
    }
    const initializedPlanDateIBEtaCutOff = initializedDates.find(
      (initializedDate) => initializedDate.planDate === format(planDate, APP_DATE_FORMAT)
    )?.ibEtaCutOff;
    this.props.handlePlanDateChanged(planDate,initializedPlanDateIBEtaCutOff);
  };

  getTerminalDate = () => {
    const {
      userData: { userProfile },
    } = this.props;
    return new Date(
      getTerminalDateByOffset(new Date(), userProfile.terminal.offset)
    );
  };

  renderDay = (day, selectedDate, dayInCurrentMonth, dayComponent) => {
    const terminalDate = this.getTerminalDate();
    const holidayName = getHolidayName(day, this.state.holidays)
    return (
      <CustomDateRenderer
        day={day}
        selectedDate={selectedDate}
        dayComponent={dayComponent}
        isInitializedPlanDate={checkPlanAlreadyInitialized(
          day,
          this.props.initializedDates
        )}
        isHoliday={isHoliday(day, this.state.holidays)}
        isDisabled={
          format(day, APP_DATE_FORMAT) < format(terminalDate, APP_DATE_FORMAT)
        }
        holidayName={holidayName}
        dayInCurrentMonth={dayInCurrentMonth}
      />
    );
  };

  cancelPlan = () => {
    this.setState({
      showHolidayWarning: false,
    });
  };

  handleConfirm = () => {
    this.props.handlePlanDateChanged(this.state.planDate);
  };

  render() {
    const { classes } = this.props;
    const planDate = this.state.planDate
      ? format(this.state.planDate, appConstants.DATETYPES.MONTHDATEYEAR.format)
      : null;
    const terminalDate = this.getTerminalDate();
    const dateInWords = format(this.state.planDate, "do MMMM");
    const dayOfWeekInWords = format(this.state.planDate, "EEEE");
    return (
      <>
        <div className={classes.container}>
          <div className={classes.dateContainer}>
            <span className={classes.label}>Date:</span>
            <DateTimeEditor
              dateType={{
                ...appConstants.DATETYPES.MONTHDATEYEAR,
                updateFormat:
                  appConstants.DATETYPES.DATETIMEWITHOUTTIMEZONE.format,
              }}
              onChange={this.onPlanDateChanged}
              value={planDate}
              minDate={terminalDate}
              renderDay={this.renderDay}
              minDateMessage="Plan date should not be before current date"
            />
          </div>
        </div>
        {this.state.showHolidayWarning && (
          <CustomConfirmationDialog
            title={`${dateInWords} (${
              isWeekend(this.state.planDate)
                ? dayOfWeekInWords
                : getHolidayName(this.state.planDate, this.state.holidays)
            }) is a holiday.`}
            cancelButtonLabel="Cancel"
            confirmButtonLabel="Create Plan"
            onConfirm={this.handleConfirm}
            onCancel={this.cancelPlan}
          >
            <div
              className={classes.confirmText}
            >{`Are you sure you want to create a plan for ${dateInWords}?`}</div>
          </CustomConfirmationDialog>
        )}
        <Footer>
          <CancelButton onClick={redirectToIBPlanning(this.props.router.navigate)} />
          <Button
            classes={{
              root: classes.actionButton,
              disabled: classes.actionDisabled,
            }}
            onClick={this.handleNext}
            disabled={
              !planDate ||
              isPastDate(this.state.planDate, this.getTerminalDate())
            }
            variant="contained"
            color="primary"
          >
            {checkPlanAlreadyInitialized(
              this.state.planDate,
              this.props.initializedDates
            )
              ? "Update Plan"
              : "Add Plan"}
          </Button>
        </Footer>
      </>
    );
  }
}

export default compose(
  withPlanDate,
  withUserProfile,
  withStyles(styles),
  withRouter,
)(PlanDateForm);
